import { useEffect, useState, type SetStateAction } from "react";
import { catchError, Observable, of, share, tap } from "rxjs";

import type { BasicQuery } from "@/core/data_sources/services";
import type { Store } from "@/core/data_sources/store";
import type { Actions } from "@/core/data_sources/store/actions";
import type { Hooks } from "@/core/data_sources/store/hooks";
import type { State } from "@/core/data_sources/store/state";

import { requestToObservable } from "../observables";
import {
  ErrorResponse,
  SuccessResponse,
  type AbortableRequest,
} from "../request";

export function useService<Q extends BasicQuery, T>(
  serviceFn: (query: Q) => AbortableRequest<T>,
  initialQuery: Q,
  errorHandler?: (err: ErrorResponse) => void,
): {
  data: SuccessResponse<T> | null;
  loading: boolean;
  error: ErrorResponse | null;
  query: Q;
  setQuery: (q: SetStateAction<Q>) => void;
} {
  const [data, setData] = useState<SuccessResponse<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const [query, setQuery] = useState(initialQuery);
  const { initialRender } = useInitialRender();
  useEffect(() => {
    if (initialRender) {
      return;
    }
    setLoading(true);
    const subs = requestToObservable(() => serviceFn(query))
      .pipe(
        share(),
        catchError((err: ErrorResponse) => {
          if (err.isCanceled()) {
            return of(null);
          }
          if (errorHandler) {
            errorHandler(err);
            return of(null);
          }
          setError(err);
          return of(null);
        }),
        tap((response) => setData(response)),
        tap(() => setLoading(false)),
      )
      .subscribe();
    return () => {
      if (subs.closed) return;
      subs.unsubscribe();
    };
    // Only needed to refresh when query changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, initialRender]);

  return { data, loading, error, setQuery, query };
}
export function useInitialRender(): { initialRender: boolean } {
  const [initialRender, setInitialRender] = useState(true);
  useEffect(() => {
    setInitialRender(false);
  }, []);
  return { initialRender };
}
export function useObservable<T>(observable$: Observable<T>): {
  data: T | null;
  isConnected: boolean;
} {
  const [data, setData] = useState<T | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const subs = observable$
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((response) => setData(response));
    setIsConnected(true);
    return () => {
      setIsConnected(false);
      if (subs.closed) return;
      subs.unsubscribe();
    };
  }, [observable$]);

  return { data, isConnected };
}
export function useStore<S, A extends Actions, H extends Hooks>(
  store: Store<State<S>, A, H>,
): {
  state: S | null;
  actions: A;
  hooks: H;
  isConnected: boolean;
} {
  const { data: state, isConnected } = useObservable(store.state.observable);
  return { state, actions: store.actions, hooks: store.hooks, isConnected };
}
